'use client';

// Create / edit dialog for a managed agent. Collects identity, brief, mode,
// schedule (via presets + raw cron), model, and the two guardrail numbers.
// Tool allowlist + connector grants are shown as read-only v1 copy — pickers
// come later; the API defaults to the safe toolset / no connectors.

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { HardDrives, EnvelopeSimple, Wrench } from '@phosphor-icons/react';
import { AGENT_SAFE_TOOL_CATALOG } from '@/lib/agent-tool-catalog';
import { ConnectorGrantsPicker } from './connector-grants-picker';
import type { AgentDraft, AgentMode, AgentSummary, MemoryAccessLevel } from './types';

// Grouped safe-tool catalog for the picker (preserves catalog order per group).
const TOOL_GROUPS: Array<{ group: string; tools: Array<{ name: string; label: string }> }> = (() => {
  const order: string[] = [];
  const byGroup = new Map<string, Array<{ name: string; label: string }>>();
  for (const t of AGENT_SAFE_TOOL_CATALOG) {
    if (!byGroup.has(t.group)) {
      byGroup.set(t.group, []);
      order.push(t.group);
    }
    byGroup.get(t.group)!.push({ name: t.name, label: t.label });
  }
  return order.map((group) => ({ group, tools: byGroup.get(group)! }));
})();

const SCHEDULE_PRESETS: Array<{ id: string; label: string; cron: string }> = [
  { id: 'manual', label: 'Manual only', cron: '' },
  { id: 'hourly', label: 'Hourly', cron: '0 * * * *' },
  { id: 'daily9', label: 'Daily 9am', cron: '0 9 * * *' },
  { id: 'weekdays9', label: 'Weekdays 9am', cron: '0 9 * * 1-5' },
];

const MODE_OPTIONS: Array<{ value: AgentMode; label: string; hint: string }> = [
  { value: 'monitor', label: 'Monitor', hint: 'Watch and only ping on something notable' },
  { value: 'digest', label: 'Digest', hint: 'Summarize on a schedule' },
  { value: 'propose', label: 'Propose', hint: 'Draft actions for you to approve' },
];

const MEMORY_ACCESS_OPTIONS: Array<{ value: MemoryAccessLevel; label: string; hint: string }> = [
  { value: 'none', label: 'None', hint: 'Only its own memory (default)' },
  { value: 'read', label: 'Read', hint: 'Can also read your memories' },
  { value: 'read_write', label: 'Read & write', hint: 'Reads + saves outcomes to your memory' },
];

const EMPTY_DRAFT: AgentDraft = {
  name: '',
  emoji: '🤖',
  brief: '',
  mode: 'digest',
  schedule: '',
  model: '',
  toolAllowlist: null,
  connectorGrants: [],
  dailyBudgetCredits: 50,
  maxStepsPerRun: 20,
  memoryAccess: 'none',
  workspaceEnabled: false,
  notifyEmail: false,
  notifyEmailAddress: '',
};

function draftFromAgent(agent: AgentSummary): AgentDraft {
  return {
    name: agent.name,
    emoji: agent.emoji || '🤖',
    brief: agent.brief,
    mode: agent.mode,
    schedule: agent.schedule || '',
    model: agent.model || '',
    toolAllowlist: agent.toolAllowlist ?? null,
    connectorGrants: agent.connectorGrants ?? [],
    dailyBudgetCredits: agent.dailyBudgetCredits,
    maxStepsPerRun: agent.maxStepsPerRun,
    memoryAccess: agent.memoryAccess ?? 'none',
    workspaceEnabled: agent.workspaceEnabled ?? false,
    notifyEmail: agent.notifyEmail ?? false,
    notifyEmailAddress: agent.notifyEmailAddress ?? '',
  };
}

export function AgentDialog({
  open,
  onOpenChange,
  editing,
  initialDraft,
  saving,
  error,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: AgentSummary | null;
  /** Prefill for a fresh create (e.g. a chosen template). Ignored when editing. */
  initialDraft?: AgentDraft | null;
  saving: boolean;
  error: string | null;
  onSubmit: (draft: AgentDraft) => void;
}) {
  const [draft, setDraft] = useState<AgentDraft>(EMPTY_DRAFT);

  // Re-seed the form whenever the dialog opens: a specific edit, else a template
  // prefill, else a blank create.
  useEffect(() => {
    if (open) setDraft(editing ? draftFromAgent(editing) : (initialDraft ?? EMPTY_DRAFT));
  }, [open, editing, initialDraft]);

  const set = <K extends keyof AgentDraft>(key: K, value: AgentDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  // Tool grants: null = default safe set; array = explicit picks.
  const customTools = draft.toolAllowlist !== null;
  const selectedTools = new Set(draft.toolAllowlist ?? []);
  const toggleTool = (name: string) => {
    const next = new Set(selectedTools);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    set('toolAllowlist', [...next]);
  };

  const activePreset =
    SCHEDULE_PRESETS.find((p) => p.cron === draft.schedule.trim())?.id ?? 'custom';

  const canSave = draft.name.trim().length > 0 && draft.brief.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit agent' : 'New agent'}</DialogTitle>
          <DialogDescription>
            An agent runs a standing brief on a schedule (or on manual wake),
            spends against a daily budget, and reports back to its own transcript.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Emoji + name */}
          <div className="flex gap-3">
            <div className="w-16 shrink-0">
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Emoji</p>
              <Input
                value={draft.emoji}
                onChange={(e) => set('emoji', e.target.value.slice(0, 8))}
                className="text-center text-lg"
                maxLength={8}
              />
            </div>
            <div className="flex-1">
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Name</p>
              <Input
                value={draft.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Morning inbox digest"
                maxLength={120}
                autoFocus
              />
            </div>
          </div>

          {/* Brief */}
          <div>
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">
              Brief — the standing instructions the agent runs each wake
            </p>
            <Textarea
              value={draft.brief}
              onChange={(e) => set('brief', e.target.value)}
              placeholder="Every morning, scan my starred sources and give me a 5-bullet digest of anything new that matters. Stay quiet if there's nothing worth my time."
              className="min-h-[120px] text-sm"
            />
          </div>

          {/* Mode */}
          <div>
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">Mode</p>
            <Select value={draft.mode} onValueChange={(v) => set('mode', v as AgentMode)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODE_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    <span className="font-medium">{m.label}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{m.hint}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Memory access */}
          <div>
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">Memory access</p>
            <Select
              value={draft.memoryAccess}
              onValueChange={(v) => set('memoryAccess', v as MemoryAccessLevel)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MEMORY_ACCESS_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    <span className="font-medium">{m.label}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{m.hint}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Controls whether this agent can see or add to your personal long-term memory. Default is None — it keeps its own memory only.
            </p>
          </div>

          {/* Persistent workspace */}
          <div className="flex items-start gap-3 rounded-lg border border-border/60 px-3 py-2.5">
            <HardDrives weight="duotone" className="mt-0.5 size-4 shrink-0 text-sky-400" />
            <div className="flex-1">
              <p className="text-xs font-medium text-foreground/90">Keep files between runs</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Give the agent a persistent workspace — one isolated sandbox whose files survive across runs. It auto-stops when idle and restarts on the next wake. Leave off for pure monitor/digest agents.
              </p>
            </div>
            <Switch
              checked={draft.workspaceEnabled}
              onCheckedChange={(v) => set('workspaceEnabled', v)}
              aria-label="Keep files between runs"
              className="mt-0.5"
            />
          </div>

          {/* Email notifications */}
          <div className="rounded-lg border border-border/60 px-3 py-2.5">
            <div className="flex items-start gap-3">
              <EnvelopeSimple weight="duotone" className="mt-0.5 size-4 shrink-0 text-violet-400" />
              <div className="flex-1">
                <p className="text-xs font-medium text-foreground/90">Email me on reports</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  When a run produces a report or proposal, email you the summary and a link to the transcript. Quiet heartbeat runs never email.
                </p>
              </div>
              <Switch
                checked={draft.notifyEmail}
                onCheckedChange={(v) => set('notifyEmail', v)}
                aria-label="Email me on reports"
                className="mt-0.5"
              />
            </div>
            {draft.notifyEmail && (
              <Input
                type="email"
                value={draft.notifyEmailAddress}
                onChange={(e) => set('notifyEmailAddress', e.target.value)}
                placeholder="Send to (leave empty for your account email)"
                className="mt-2.5 text-xs"
              />
            )}
          </div>

          {/* Schedule */}
          <div>
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">Schedule</p>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {SCHEDULE_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => set('schedule', preset.cron)}
                  className={`rounded-full px-3 py-1 text-[12px] transition-colors ${
                    activePreset === preset.id
                      ? 'bg-foreground font-bold text-background'
                      : 'border border-border/70 font-medium text-muted-foreground hover:border-border hover:text-foreground'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <Input
              value={draft.schedule}
              onChange={(e) => set('schedule', e.target.value)}
              placeholder="Cron (leave empty for manual-only) — e.g. 0 9 * * *"
              className="font-mono text-xs"
            />
          </div>

          {/* Model + budget + steps */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Model</p>
              <Input
                value={draft.model}
                onChange={(e) => set('model', e.target.value)}
                placeholder="platform default"
                className="text-xs"
              />
            </div>
            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Daily budget (cr)</p>
              <Input
                type="number"
                min={1}
                value={draft.dailyBudgetCredits}
                onChange={(e) => set('dailyBudgetCredits', Math.max(1, Number(e.target.value) || 1))}
              />
            </div>
            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Max steps / run</p>
              <Input
                type="number"
                min={1}
                max={50}
                value={draft.maxStepsPerRun}
                onChange={(e) =>
                  set('maxStepsPerRun', Math.min(50, Math.max(1, Number(e.target.value) || 1)))
                }
              />
            </div>
          </div>

          {/* Tools — default safe set, or a custom allowlist */}
          <div className="rounded-lg border border-border/60 px-3 py-2.5">
            <div className="flex items-start gap-3">
              <Wrench weight="duotone" className="mt-0.5 size-4 shrink-0 text-emerald-400" />
              <div className="flex-1">
                <p className="text-xs font-medium text-foreground/90">Tools</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {customTools
                    ? 'Custom — the agent may use only the tools you check below.'
                    : 'Default safe set — read-only web, search, docs, media and visualization tools.'}
                </p>
              </div>
              <Switch
                checked={customTools}
                onCheckedChange={(v) => set('toolAllowlist', v ? [...selectedTools] : null)}
                aria-label="Customize tools"
                className="mt-0.5"
              />
            </div>
            {customTools && (
              <div className="mt-3 space-y-3">
                {TOOL_GROUPS.map(({ group, tools }) => (
                  <div key={group}>
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground/70">
                      {group}
                    </p>
                    <div className="grid grid-cols-2 gap-1">
                      {tools.map((t) => (
                        <label
                          key={t.name}
                          className="flex cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-1 text-[12px] hover:bg-muted/40"
                        >
                          <input
                            type="checkbox"
                            checked={selectedTools.has(t.name)}
                            onChange={() => toggleTool(t.name)}
                            className="size-3.5 accent-emerald-500"
                          />
                          <span className="truncate">{t.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                {selectedTools.size === 0 && (
                  <p className="text-[11px] text-amber-400">
                    No tools checked — this agent will have no tools. Check at least one, or turn off Custom for the safe set.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Connectors — live picker of the user's connected Composio toolkits,
              with a free-text escape hatch for unlisted slugs. */}
          <ConnectorGrantsPicker
            open={open}
            grants={draft.connectorGrants}
            onChange={(next) => set('connectorGrants', next)}
          />

          {error && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={saving || !canSave}
              onClick={() => onSubmit(draft)}
              className="rounded-full bg-violet-500 px-4 font-semibold text-white hover:bg-violet-400"
            >
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create agent'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
